import { useEffect, useState } from "react";
import { useBatteryEvents } from "./useBatteryEvents";
import { formatDateTime, getUniqueItems, sortComparatorDateTimeString } from "../../utils";
import { GridColDef } from '@mui/x-data-grid';
import { BatteryEvents } from "./BatteryEvents";
import { BatteryEventDto } from "./dtos/BatteryEventDto";
import type {} from '@mui/x-data-grid/themeAugmentation';
import { batteryEvents } from "../../data/batteryEvents.data";
import { BatteryEventLatestDto } from "./dtos/BatteryEventLatestDto";
import { DashboardEventsLatest } from "../../components/DashboardEventsLatest";
import { Dashboard } from "../../enums/Dashboard";


type DataRow = {

    id: number;
    doorCode: string;
    doorName: string;
    eventDateTime: Date;
    voltage: string;
    voltageLevel: number;

};


const getColumns = ( rows: DataRow[] ): GridColDef[ ] => {

    // do not display voltage column if only one voltage was selected
    const distinctVoltages = getUniqueItems( rows.map( row => row.voltage ) );

    let columns: GridColDef[ ] = [ ];

    
    if( distinctVoltages.length === 1 && distinctVoltages[0] === 'Unknown' ) {

        columns = [
            
            {
                field: 'doorName',
                headerName: 'Door',
                width: 150,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            }

        ];

    } else if( distinctVoltages.length === 1 ) {

        columns = [

            {
                field: 'eventDateTime',
                headerName: 'Event Date Time',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center',
                renderCell: ( params => formatDateTime( params.row.eventDateTime )),
                sortComparator: sortComparatorDateTimeString,
            },
            
            {
                field: 'doorName',
                headerName: 'Door',
                width: 150,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            }

        ];

    } else {

        columns = [

            {
                field: 'eventDateTime',
                headerName: 'Event Date Time',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center',
                renderCell: ( params => formatDateTime( params.row.eventDateTime ))
            },

            {
                field: 'doorName',
                headerName: 'Door',
                width: 150,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'voltage',
                headerName: 'Voltage Level',
                width: 150,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            }

        ];

    }

    return columns

};


const getRows = ( batteryEventsLatest: BatteryEventLatestDto[] ) : DataRow[] => {

    const rows: DataRow[] = [];
    let i = 0;

    for( const batteryEventLatest of batteryEventsLatest ) {

        const batteryEvent = batteryEvents.find( be => be.event === batteryEventLatest.event );

        if( !batteryEvent ) {
            
            throw Error( `Battery event ${batteryEventLatest.event} not found` );

        }

        rows.push({ 

            doorCode: batteryEventLatest.doorCode, 
            doorName: batteryEventLatest.doorName, 
            eventDateTime: batteryEventLatest.eventDateTime,
            id: i,
            voltage: batteryEvent.displayedText, 
            voltageLevel: batteryEventLatest.voltageLevel 

        });

        i++;

    }

    return rows;

};


type BatteryEventsLatestProps = {

    events: string[];
    manualRefreshCount: number;
    refreshCount: number;
    villageCode: string;

};


export const BatteryEventsLatest: React.FC<BatteryEventsLatestProps> = ( props : BatteryEventsLatestProps ) => {

    const { events, manualRefreshCount, refreshCount, villageCode  } = props;

    const [ areBatteryEventsLatestLoading, setAreBatteryEventsLatestLoading ] = useState<boolean>( false );
    const [ batteryEventDtos, setBatteryEventDtos ] = useState<BatteryEventDto[]>( [] );
    const [ columns, setColumns ] = useState< GridColDef[ ] >( [ ] );
    const [ displayBatteryEvents, setDisplayBatteryEvents ] = useState( false );
    const [ doorName, setDoorName ] = useState<string>( '' );
    const [ isChildLoading, setIsChildLoading ] = useState( false );
    const [ rows, setRows ] = useState<DataRow[ ]>( [ ] );

    const { getBatteryEvents, getBatteryEventsLatest } = useBatteryEvents( );

    const style = {

        width: 
            ( columns.length === 1 ) 
                ? 
            250 
                : 
            ( columns.length === 2 ) ? 390 : 505

    };


    useEffect( ( ) => {

        if( events.length === 0 || !villageCode || manualRefreshCount === 0 ) {

            setRows( [] );
            return;

        }

        const fetchBatteryEventsLatest = async( ) => {

            setAreBatteryEventsLatestLoading( true );
            const batteryEventsLatest = await getBatteryEventsLatest( villageCode, events );
            setAreBatteryEventsLatestLoading( false );

            processBatteryEventsLatest( batteryEventsLatest);

        };

        fetchBatteryEventsLatest( );

    }, [ manualRefreshCount ]);




    useEffect( ( ) => {

        if( events.length === 0 || !villageCode ) {

            setRows( [] );
            return;

        }

        const fetchBatteryEventsLatest = async( ) => {

            if( refreshCount === 0 ) {
            
                setAreBatteryEventsLatestLoading( true );

            }

            const batteryEventsLatest : BatteryEventLatestDto[] | null = await getBatteryEventsLatest( villageCode, events );

            if( refreshCount === 0 ) {
                
                setAreBatteryEventsLatestLoading( false );

            }

            processBatteryEventsLatest( batteryEventsLatest);

        };

        fetchBatteryEventsLatest( );

    }, [ events, refreshCount, villageCode ]);


    const processBatteryEventsLatest = ( batteryEventsLatest : BatteryEventLatestDto[] | null ) => {

        if( batteryEventsLatest ) {

            const rows = getRows( batteryEventsLatest );
            
            setRows( rows );

            const columns = getColumns( rows );
            setColumns( columns );

        }

    };


    const selectRowHandler = async ( rowId: string ) => {

        const row = rows.find( r => r.id === +rowId );

        if( !row  ) {

            throw Error( `Cannot find row with id = ${rowId}` );

        }

        if( row.voltage === 'Unknown' ) { // Unknown voltage level is non-clickable
            
            return;

        }

        setIsChildLoading( true );
        const batteryEventDtos = await getBatteryEvents( row.doorCode, villageCode );
        setIsChildLoading( false );

        if( !batteryEventDtos ) {

            return;

        }

        setBatteryEventDtos( batteryEventDtos );
        setDoorName( row.doorName );
        setDisplayBatteryEvents( true );

    };


    const handleClose = ( ) => setDisplayBatteryEvents( false );


    return(

        <>

            <DashboardEventsLatest 
                
                columns={ columns } 
                dashboard={ Dashboard.BatteryLevel }
                isChildLoading={ isChildLoading } 
                isParentLoading={ areBatteryEventsLatestLoading } 
                rows={ rows }
                selectRow={ selectRowHandler }
                sortField="eventDateTime"
                style={ style } 
            />

            <BatteryEvents displayBatteryEvents={displayBatteryEvents} doorName={doorName} onClose={handleClose} batteryEventDtos={batteryEventDtos} />

        </>

    );

};
